// Profil gamifikasi per nama (dipakai klien dan server).
// Semua angka disimpan PER HARI supaya profil dari dua perangkat bisa digabung tanpa kehilangan
// data: XP, streak, lencana, dan level dihitung ulang dari catatan harian (src/game/aturan.js).
//
// Bentuk:
// { versi: 1,
//   hari: { 'YYYY-MM-DD': {
//     s: soal dijawab, b: benar, bm: benar Matematika, bi: benar Bahasa Indonesia,
//     r: ronde selesai, n: [nilai ronde], m: [mapel yang dilatih], k: kombo terbaik hari itu,
//     mo: { idModul: bintang } modul selesai, l: langkah belajar, p: pembahasan diselesaikan,
//     sa: ronde latihan dari saran peta kemampuan, kt: peti harian dibuka, st: [stiker didapat],
//     kw: kunci pekan bila peti pekan dibuka hari itu } },
//   topik: { mapel: { kunciTopik: { n, b, h: '1011…' (10 jawaban terakhir, terbaru di akhir) } } },
//   belajar: { idModul: { t: langkah terakhir, s: selesai, b: bintang terbaik, w: waktu terakhir (ms) } },
//   kostum: 'polos', kostumWaktu: ms, dilihat: { lencana: { id: tingkat }, level, isiAwal } }

export const POLA_TANGGAL = /^\d{4}-\d{2}-\d{2}$/;
export const BATAS_PROFIL = 256 * 1024;
const POLA_ID = /^[a-z0-9-]{1,40}$/;
const ANGKA_HARI = ['s', 'b', 'bm', 'bi', 'r', 'k', 'l', 'p', 'sa'];

const bulat = (v, maks = 1e6) => (Number.isFinite(v) && v > 0 ? Math.min(maks, Math.floor(v)) : 0);
const daftarId = (a, maks = 30) => (Array.isArray(a) ? [...new Set(a.filter(x => typeof x === 'string' && POLA_ID.test(x)))].slice(0, maks) : []);

/** Tanggal lokal perangkat 'YYYY-MM-DD'. */
export const tanggalLokal = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const profilKosong = () => ({ versi: 1, hari: {}, topik: {}, belajar: {}, kostum: 'polos', kostumWaktu: 0, dilihat: {} });

function bersihHari(h) {
  if (!h || typeof h !== 'object') return null;
  const o = {};
  for (const k of ANGKA_HARI) { const v = bulat(h[k]); if (v) o[k] = v; }
  if (Array.isArray(h.n)) { const n = h.n.filter(x => Number.isFinite(x) && x >= 0 && x <= 100).map(Math.round).slice(0, 50); if (n.length) o.n = n; }
  const m = daftarId(h.m, 5); if (m.length) o.m = m;
  if (h.mo && typeof h.mo === 'object') {
    const mo = Object.fromEntries(Object.entries(h.mo).filter(([id, b]) => POLA_ID.test(id) && [1, 2, 3].includes(b)).slice(0, 40));
    if (Object.keys(mo).length) o.mo = mo;
  }
  if (h.kt === true) o.kt = true;
  const st = daftarId(h.st, 5); if (st.length) o.st = st;
  if (typeof h.kw === 'string' && POLA_TANGGAL.test(h.kw)) o.kw = h.kw;
  return Object.keys(o).length ? o : null;
}

/** Bersihkan profil dari luar (localStorage lama atau kiriman klien). */
export function bersihkanProfil(raw) {
  const p = profilKosong();
  if (!raw || typeof raw !== 'object') return p;
  if (raw.hari && typeof raw.hari === 'object') {
    for (const [t, h] of Object.entries(raw.hari).slice(-800)) { if (!POLA_TANGGAL.test(t)) continue; const b = bersihHari(h); if (b) p.hari[t] = b; }
  }
  if (raw.topik && typeof raw.topik === 'object') {
    for (const [mapel, ts] of Object.entries(raw.topik)) {
      if (!POLA_ID.test(mapel) || !ts || typeof ts !== 'object') continue;
      for (const [k, v] of Object.entries(ts).slice(0, 80)) {
        if (!/^[a-z0-9-]{1,40}$/.test(k) || !v || typeof v !== 'object') continue;
        const n = bulat(v.n), b = Math.min(n, bulat(v.b)), h = typeof v.h === 'string' ? v.h.replace(/[^01]/g, '').slice(-10) : '';
        if (n) (p.topik[mapel] ||= {})[k] = { n, b, h };
      }
    }
  }
  if (raw.belajar && typeof raw.belajar === 'object') {
    for (const [id, m] of Object.entries(raw.belajar).slice(0, 200)) {
      if (!POLA_ID.test(id) || !m || typeof m !== 'object') continue;
      const b = { t: bulat(m.t, 60), w: bulat(m.w, 1e15) };
      if (m.s === true) b.s = true;
      if ([1, 2, 3].includes(m.b)) b.b = m.b;
      p.belajar[id] = b;
    }
  }
  if (typeof raw.kostum === 'string' && POLA_ID.test(raw.kostum)) p.kostum = raw.kostum;
  p.kostumWaktu = bulat(raw.kostumWaktu, 1e15);
  const d = raw.dilihat;
  if (d && typeof d === 'object') {
    if (d.lencana && typeof d.lencana === 'object') p.dilihat.lencana = Object.fromEntries(Object.entries(d.lencana).filter(([id, t]) => POLA_ID.test(id) && [1, 2, 3].includes(t)));
    if (bulat(d.level, 1000)) p.dilihat.level = bulat(d.level, 1000);
    if (d.isiAwal === true) p.dilihat.isiAwal = true;
  }
  return p;
}

// Gabung dua catatan hari: angka diambil yang terbesar (perangkat yang sama tidak terhitung dua kali),
// daftar digabung, bintang modul diambil yang tertinggi.
function gabungHari(a = {}, b = {}) {
  const o = {};
  for (const k of ANGKA_HARI) { const v = Math.max(a[k] || 0, b[k] || 0); if (v) o[k] = v; }
  const n = (a.n?.length || 0) >= (b.n?.length || 0) ? a.n : b.n; if (n?.length) o.n = n;
  const m = [...new Set([...(a.m || []), ...(b.m || [])])]; if (m.length) o.m = m;
  const mo = { ...(a.mo || {}) }; for (const [id, x] of Object.entries(b.mo || {})) mo[id] = Math.max(mo[id] || 0, x); if (Object.keys(mo).length) o.mo = mo;
  if (a.kt || b.kt) o.kt = true;
  const st = [...new Set([...(a.st || []), ...(b.st || [])])]; if (st.length) o.st = st;
  if (a.kw || b.kw) o.kw = a.kw || b.kw;
  return o;
}

/** Gabung profil dua perangkat (komutatif untuk data harian). */
export function gabungProfil(a, b) {
  a = bersihkanProfil(a); b = bersihkanProfil(b);
  const p = profilKosong();
  for (const t of new Set([...Object.keys(a.hari), ...Object.keys(b.hari)])) p.hari[t] = gabungHari(a.hari[t], b.hari[t]);
  for (const mapel of new Set([...Object.keys(a.topik), ...Object.keys(b.topik)])) {
    const x = a.topik[mapel] || {}, y = b.topik[mapel] || {};
    p.topik[mapel] = {};
    for (const k of new Set([...Object.keys(x), ...Object.keys(y)])) p.topik[mapel][k] = (x[k]?.n || 0) >= (y[k]?.n || 0) ? x[k] : y[k];
  }
  // Progres modul: selesai & bintang terbaik dari perangkat mana pun; langkah terakhir dari yang terbaru.
  for (const id of new Set([...Object.keys(a.belajar), ...Object.keys(b.belajar)])) {
    const x = a.belajar[id], y = b.belajar[id], baru = !y || (x && x.w >= y.w) ? x : y;
    const m = { t: baru.t, w: Math.max(x?.w || 0, y?.w || 0) };
    if (x?.s || y?.s) m.s = true;
    const bintang = Math.max(x?.b || 0, y?.b || 0); if (bintang) m.b = bintang;
    p.belajar[id] = m;
  }
  const baru = (a.kostumWaktu || 0) >= (b.kostumWaktu || 0) ? a : b;
  p.kostum = baru.kostum; p.kostumWaktu = baru.kostumWaktu;
  const la = a.dilihat.lencana || {}, lb = b.dilihat.lencana || {};
  const lencana = { ...la }; for (const [id, t] of Object.entries(lb)) lencana[id] = Math.max(lencana[id] || 0, t);
  p.dilihat = { ...(Object.keys(lencana).length ? { lencana } : {}), ...(Math.max(a.dilihat.level || 0, b.dilihat.level || 0) ? { level: Math.max(a.dilihat.level || 0, b.dilihat.level || 0) } : {}), ...(a.dilihat.isiAwal || b.dilihat.isiAwal ? { isiAwal: true } : {}) };
  return p;
}

export const kunciProfil = slug => `profil/${slug}.json`;
